import { useChapterHeadings } from "./useChapterHeadings";
import { computed, nextTick, ref, type ComputedRef, type Ref } from "vue";
import { buildBookDocx } from "../export/buildDocx";
import { buildBookEpub } from "../export/buildEpub";
import { buildPdfBook, pdfBookToNativeJson } from "../export/pdfBook";
import { enrichPdfBookHeadingImages } from "../export/pdfBookAssets";
import {
  buildBookMarkdown,
  type MarkdownBookChapter,
} from "../export/buildMarkdown";
import {
  manuscriptExportLeaves,
  type BookBlock,
  type BookChapter,
} from "../export/buildBook";
import { sceneOpening } from "../export/chapterOpening";
import { prepareMapFigureExport } from "../export/mapFigureExport";
import { assembleChapterHtml } from "../export/sceneFormatting";
import { zwdocToExportHtml } from "../export/zwdocHtml";
import { emptyZwDocNode } from "../lib/zwdoc/format";
import { anyToZwDoc } from "../lib/zwdoc/review";
import type { EditorFontId } from "../editor/fonts";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import {
  DEFAULT_PRINT_SIZE,
  PROJECT_CHAPTERS_DIR,
  defaultZwCopyrightPage,
  defaultZwDedicationPage,
  defaultZwPageChrome,
  defaultZwParagraphFormatting,
  defaultZwSceneFormatting,
  isZwPrintSize,
  type ZwChapter,
  type ZwPrintSize,
  type ZwParagraphFormatting,
  type ZwSceneFormatting,
} from "../lib/zwConfig";
import { assertNever } from "../lib/assertNever";
import { isTauri, joinProjectPath } from "../lib/runtime";
import { readProjectFile } from "../lib/tauri/core";
import {
  saveBookDocx,
  saveBookEpub,
  saveBookMarkdown,
  saveBookPdfNative,
} from "../lib/tauri/export";
import {
  dismissExportToast,
  reportSavedExport,
  reportExportFailed,
  reportExportInfo,
} from "./useExportToast";
import { formatExportFailure } from "./useExportToast.update";
import { useEditorSaveStore } from "../stores/useEditorSaveStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";

export type ProjectExportContext = {
  exportBusy: Ref<boolean>;
  /** Dialog title while an export is in flight (empty when idle). */
  exportBusyTitle: Ref<string>;
  /** Dialog body while an export is in flight (empty when idle). */
  exportBusyMessage: Ref<string>;
  exportDefaults: ComputedRef<{
    title: string;
    author: string;
    printSize: ZwPrintSize;
  }>;
  exportPdf: () => Promise<void>;
  exportEpub: () => Promise<void>;
  exportDocx: () => Promise<void>;
  exportMarkdown: () => Promise<void>;
};

type CreateProjectExportOptions = {
  bodyFontId: Ref<EditorFontId>;
  headingFontId: Ref<EditorFontId>;
};

export function createProjectExport(
  options: CreateProjectExportOptions,
): ProjectExportContext {
  const session = useEditorSessionStore();
  const save = useEditorSaveStore();
  const { headingFor } = useChapterHeadings();
  function exportChapter(chapter: ZwChapter): ZwChapter {
    return {
      ...chapter,
      name: matchMaybe(headingFor(chapter.folder), {
        just: (heading) => heading.lines.join("\n"),
        nothing: () => chapter.name,
      }),
    };
  }
  const { bodyFontId, headingFontId } = options;
  const exportBusy = ref(false);
  const exportBusyTitle = ref("");
  const exportBusyMessage = ref("");

  async function startExportBusy(
    title: string,
    message: string,
  ): Promise<void> {
    exportBusy.value = true;
    exportBusyTitle.value = title;
    exportBusyMessage.value = message;
    // Let the busy dialog paint before sync-heavy book work.
    await nextTick();
  }

  function endExportBusy(): void {
    exportBusy.value = false;
    exportBusyTitle.value = "";
    exportBusyMessage.value = "";
  }

  const exportDefaults = computed(() => {
    const fallbackTitle =
      session.projectName.length > 0
        ? session.projectName
        : session.folderName.length > 0
          ? session.folderName
          : "Untitled";
    return matchMaybe(session.projectConfig, {
      just: (config) => {
        const meta = config.metadata;
        const trimmedTitle = meta.title.trim();
        const title = trimmedTitle.length > 0 ? trimmedTitle : fallbackTitle;
        const author = meta.author.trim();
        const printSize = isZwPrintSize(config.printSize)
          ? config.printSize
          : DEFAULT_PRINT_SIZE;
        return { title, author, printSize };
      },
      nothing: () => ({
        title: fallbackTitle,
        author: "",
        printSize: DEFAULT_PRINT_SIZE,
      }),
    });
  });

  async function loadChapterScenes(
    root: string,
    chapter: ZwChapter,
  ): Promise<ReturnType<typeof emptyZwDocNode>[]> {
    return Promise.all(
      chapter.scenes.map(async (scene) => {
        const scenePath = `${PROJECT_CHAPTERS_DIR}/${chapter.folder}/${scene.file}`;
        const text =
          isJust(session.activeBasename) &&
          scenePath === session.activeBasename.value
            ? session.doc
            : await readProjectFile(joinProjectPath(root, scenePath));
        const parsed = anyToZwDoc(text);
        return parsed.ok ? parsed.value.doc : emptyZwDocNode();
      }),
    );
  }

  /**
   * Every chapter's scene documents, in manuscript order. Scenes are
   * `.zwdoc`; anything else goes through the #248 converter, and an
   * unreadable scene exports empty rather than failing the whole book.
   * Markdown export stays chapter-only (maps are binary figures).
   */
  async function gatherManuscript(): Promise<MarkdownBookChapter[]> {
    if (isNothing(session.rootPath)) throw new Error("Missing project root");
    const root = session.rootPath.value;
    const leaves = manuscriptExportLeaves(session.manuscript);
    const chapters = leaves.filter(
      (leaf): leaf is Extract<typeof leaf, { type: "chapter" }> =>
        leaf.type === "chapter",
    );
    return Promise.all(
      chapters.map(async ({ chapter }) => ({
        chapter: exportChapter(chapter),
        scenes: await loadChapterScenes(root, chapter),
      })),
    );
  }

  function sceneFormatting(): ZwSceneFormatting {
    return matchMaybe(session.projectConfig, {
      just: (config) => config.sceneFormatting ?? defaultZwSceneFormatting(),
      nothing: () => defaultZwSceneFormatting(),
    });
  }

  function paragraphFormatting(): ZwParagraphFormatting {
    return matchMaybe(session.projectConfig, {
      just: (config) => config.paragraphFormatting,
      nothing: () => defaultZwParagraphFormatting(),
    });
  }

  function chapterToBookChapter(
    chapter: ZwChapter,
    scenes: Awaited<ReturnType<typeof loadChapterScenes>>,
  ): BookChapter {
    const formatting = sceneFormatting();
    const paragraphs = paragraphFormatting();
    return {
      name: exportChapter(chapter).name,
      html: assembleChapterHtml(
        chapter,
        scenes.map((doc, index) =>
          zwdocToExportHtml(doc, sceneOpening(paragraphs, index === 0)),
        ),
        formatting,
      ),
    };
  }

  /**
   * Full manuscript walk for PDF / EPUB / DOCX: chapters and sequence maps
   * in outline order (#177). Map PNGs prepared once at 300 DPI.
   */
  async function gatherExportBlocks(
    printSize: ZwPrintSize,
  ): Promise<BookBlock[]> {
    if (isNothing(session.rootPath)) throw new Error("Missing project root");
    const root = session.rootPath.value;
    const leaves = manuscriptExportLeaves(session.manuscript);
    const blocks: BookBlock[] = [];
    for (const leaf of leaves) {
      switch (leaf.type) {
        case "chapter": {
          const scenes = await loadChapterScenes(root, leaf.chapter);
          blocks.push({
            type: "chapter",
            ...chapterToBookChapter(leaf.chapter, scenes),
          });
          break;
        }
        case "map": {
          const prepared = await prepareMapFigureExport(leaf.figure, {
            rootPath: root,
            printSize,
          });
          blocks.push({ type: "map", prepared });
          break;
        }
        default:
          assertNever(leaf);
      }
    }
    return blocks;
  }

  function hasExportableManuscript(): boolean {
    return manuscriptExportLeaves(session.manuscript).length > 0;
  }

  function beginExport(format: "PDF" | "EPUB" | "DOCX" | "Markdown"): Maybe<{
    title: string;
    author: string;
    printSize: ZwPrintSize;
  }> {
    dismissExportToast();
    if (
      isTauri() === false ||
      isNothing(session.rootPath) ||
      isNothing(session.projectConfig)
    ) {
      reportExportInfo(`${format} export is available for project folders.`);
      return nothing;
    }
    if (hasExportableManuscript() === false) {
      reportExportInfo("Add at least one chapter or map before exporting.");
      return nothing;
    }
    if (exportBusy.value === true) return nothing;

    return just(exportDefaults.value);
  }

  async function exportPdf(): Promise<void> {
    const defaultsM = beginExport("PDF");
    if (
      isNothing(defaultsM) ||
      isNothing(session.projectConfig) ||
      isNothing(session.rootPath)
    ) {
      return;
    }
    const config = session.projectConfig.value;
    const { title, author, printSize } = defaultsM.value;

    try {
      await startExportBusy(
        "Writing PDF",
        "Building your book. This can take a moment for longer manuscripts or maps.",
      );
      await save.flushSave();
      // #289/#292/#294/#295: native PDF (chapters, headings, openings, maps,
      // front matter). EPUB and DOCX stay on their own builders.
      const root = session.rootPath.value;
      const outlineLeaves = manuscriptExportLeaves(session.manuscript);
      const leaves = [];
      for (const leaf of outlineLeaves) {
        switch (leaf.type) {
          case "chapter": {
            const scenes = await loadChapterScenes(root, leaf.chapter);
            leaves.push({
              type: "chapter" as const,
              chapter: exportChapter(leaf.chapter),
              scenes,
            });
            break;
          }
          case "map": {
            const prepared = await prepareMapFigureExport(leaf.figure, {
              rootPath: root,
              printSize,
            });
            leaves.push({ type: "map" as const, prepared });
            break;
          }
          default:
            assertNever(leaf);
        }
      }
      const built = buildPdfBook({
        title,
        author,
        subtitle: config.metadata.subtitle,
        printSize,
        paragraphFormatting: config.paragraphFormatting,
        sceneFormatting: sceneFormatting(),
        chapterHeadingStyle: config.chapterHeadingStyle,
        pageChrome: config.pageChrome ?? defaultZwPageChrome(),
        bodyFontId: bodyFontId.value,
        headingFontId: headingFontId.value,
        metadata: config.metadata,
        copyrightPage: config.copyrightPage ?? defaultZwCopyrightPage(),
        dedicationPage: config.dedicationPage ?? defaultZwDedicationPage(),
        leaves,
      });
      if (built.body.length === 0) {
        reportExportInfo(
          "Add at least one chapter or map before exporting PDF.",
        );
        return;
      }
      const book = await enrichPdfBookHeadingImages(built, root);
      reportSavedExport(
        "PDF",
        await saveBookPdfNative(pdfBookToNativeJson(book), title),
      );
    } catch (error) {
      console.error("Failed to export PDF", error);
      reportExportFailed(formatExportFailure("Could not export PDF.", error));
    } finally {
      endExportBusy();
    }
  }

  async function exportEpub(): Promise<void> {
    const defaultsM = beginExport("EPUB");
    if (
      isNothing(defaultsM) ||
      isNothing(session.projectConfig) ||
      isNothing(session.rootPath)
    ) {
      return;
    }
    const config = session.projectConfig.value;
    const root = session.rootPath.value;
    const { title, author, printSize } = defaultsM.value;

    await startExportBusy(
      "Exporting EPUB",
      "Building your ebook. This can take a moment for longer manuscripts.",
    );
    try {
      await save.flushSave();
      const blocks = await gatherExportBlocks(printSize);
      const bytes = await buildBookEpub({
        title,
        subtitle: config.metadata.subtitle,
        author,
        paragraphFormatting: config.paragraphFormatting,
        chapterHeadingStyle: config.chapterHeadingStyle,
        copyrightPage: config.copyrightPage,
        dedicationPage: config.dedicationPage,
        metadata: config.metadata,
        bodyFontId: bodyFontId.value,
        headingFontId: headingFontId.value,
        rootPath: root,
        blocks,
      });
      reportSavedExport("EPUB", await saveBookEpub(bytes, title));
    } catch (error) {
      console.error("Failed to export EPUB", error);
      reportExportFailed(formatExportFailure("Could not export EPUB.", error));
    } finally {
      endExportBusy();
    }
  }

  async function exportDocx(): Promise<void> {
    const defaultsM = beginExport("DOCX");
    if (
      isNothing(defaultsM) ||
      isNothing(session.projectConfig) ||
      isNothing(session.rootPath)
    ) {
      return;
    }
    const config = session.projectConfig.value;
    const root = session.rootPath.value;
    const { title, author, printSize } = defaultsM.value;

    await startExportBusy(
      "Exporting DOCX",
      "Building your Word document. This can take a moment for longer manuscripts.",
    );
    try {
      await save.flushSave();
      const blocks = await gatherExportBlocks(printSize);
      const bytes = await buildBookDocx({
        title,
        subtitle: config.metadata.subtitle,
        author,
        printSize,
        pageChrome: config.pageChrome,
        paragraphFormatting: config.paragraphFormatting,
        chapterHeadingStyle: config.chapterHeadingStyle,
        copyrightPage: config.copyrightPage,
        dedicationPage: config.dedicationPage,
        metadata: config.metadata,
        bodyFontId: bodyFontId.value,
        headingFontId: headingFontId.value,
        rootPath: root,
        blocks,
      });
      reportSavedExport("DOCX", await saveBookDocx(bytes, title));
    } catch (error) {
      console.error("Failed to export DOCX", error);
      reportExportFailed(formatExportFailure("Could not export DOCX.", error));
    } finally {
      endExportBusy();
    }
  }

  async function exportMarkdown(): Promise<void> {
    const defaultsM = beginExport("Markdown");
    if (isNothing(defaultsM) || isNothing(session.projectConfig)) return;
    const config = session.projectConfig.value;
    const { title, author } = defaultsM.value;

    await startExportBusy(
      "Exporting Markdown",
      "Building your Markdown export. Please wait.",
    );
    try {
      await save.flushSave();
      const markdown = buildBookMarkdown({
        title,
        subtitle: config.metadata.subtitle,
        author,
        copyrightPage: config.copyrightPage,
        dedicationPage: config.dedicationPage,
        metadata: config.metadata,
        sceneFormatting: sceneFormatting(),
        chapters: await gatherManuscript(),
      });
      reportSavedExport("Markdown", await saveBookMarkdown(markdown, title));
    } catch (error) {
      console.error("Failed to export Markdown", error);
      reportExportFailed(
        formatExportFailure("Could not export Markdown.", error),
      );
    } finally {
      endExportBusy();
    }
  }

  return {
    exportBusy,
    exportBusyTitle,
    exportBusyMessage,
    exportDefaults,
    exportPdf,
    exportEpub,
    exportDocx,
    exportMarkdown,
  };
}
